<script setup lang="ts">
/**
 * BlogInlineCta — le pont entre un article et la page produit de SON besoin.
 *
 * Mesuré en production sur 60 jours : 591 sessions arrivent sur un article et
 * repartent sans voir une seule autre page ; DEUX seulement ont trouvé le
 * chemin vers /projets/tombola. Pourtant le même sujet converti sur la page
 * produit donne 14 à 29 % de passage vers l'inscription, contre 0 % sur
 * l'article. Ce bloc existe pour fermer cet écart.
 *
 * Choix de conception, tous dictés par « ne pas casser le référencement » :
 *   · rendu SERVEUR, jamais injecté après hydratation — Google le voit ;
 *   · posé ENTRE deux blocs de contenu, à une frontière de titre `<h2>`,
 *     donc jamais au milieu d'une phrase ni d'un tableau ;
 *   · aucun titre `<h2>`/`<h3>` à l'intérieur : la structure de titres de
 *     l'article, qui le fait ranker, reste strictement inchangée. Le titre du
 *     bloc est un `<p>` mis en forme ;
 *   · il pointe vers une page PRODUIT, pas vers /register : le lecteur n'est
 *     pas encore prêt à créer un compte, il veut voir comment on fait.
 *
 * Le clic est mesuré (`cta_click`) avec l'emplacement et la destination, via
 * la directive `v-register-cta` — c'est elle qui dira si ce bloc travaille.
 */
import { blogCtaTarget } from "~/utils/blogCtaTarget";

const props = defineProps<{
  /** Slug de l'article — détermine la page produit proposée. */
  slug: string | null | undefined;
  /** Où le bloc est posé : sert uniquement à la mesure. */
  emplacement: "milieu-article" | "fin-article";
}>();

const cible = computed(() => blogCtaTarget(props.slug));
</script>

<template>
  <aside
    class="blog-inline-cta"
    :aria-label="cible.titre"
  >
    <p class="blog-inline-cta__titre">{{ cible.titre }}</p>
    <p class="blog-inline-cta__texte">{{ cible.texte }}</p>
    <NuxtLink
      :to="cible.to"
      v-register-cta="`${emplacement}:${cible.to}`"
      class="blog-inline-cta__bouton"
    >
      {{ cible.bouton }}
      <UIcon name="i-heroicons-arrow-right" class="w-4 h-4" />
    </NuxtLink>
  </aside>
</template>

<style scoped>
/* Volontairement sobre : le bloc doit se lire comme un encadré de l'article,
   pas comme une bannière publicitaire — une bannière se saute des yeux. */
.blog-inline-cta {
  margin: 2.5rem 0;
  padding: 1.5rem 1.75rem;
  border: 1px solid var(--color-main, #0c3097);
  border-left-width: 4px;
  border-radius: 0.75rem;
  background: color-mix(in srgb, var(--color-main, #0c3097) 4%, transparent);
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  align-items: flex-start;
}

.blog-inline-cta__titre {
  margin: 0;
  font-size: 1.0625rem;
  font-weight: 700;
  line-height: 1.35;
  color: var(--color-ink, #111827);
  text-wrap: balance;
}

.blog-inline-cta__texte {
  margin: 0;
  font-size: 0.9375rem;
  line-height: 1.6;
  color: color-mix(in srgb, var(--color-ink, #111827) 72%, transparent);
  max-width: 62ch;
}

.blog-inline-cta__bouton {
  margin-top: 0.375rem;
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.625rem 1.25rem;
  border-radius: 9999px;
  background: var(--color-main, #0c3097);
  color: #fff;
  font-size: 0.9375rem;
  font-weight: 600;
  text-decoration: none;
  transition: background-color 0.15s ease;
}

.blog-inline-cta__bouton:hover {
  background: var(--color-main-deep, #081f63);
}

.blog-inline-cta__bouton:focus-visible {
  outline: 2px solid var(--color-main, #0c3097);
  outline-offset: 3px;
}
</style>
